import { useEffect, useState } from 'react';
import { Icon, Modal } from '@/components/kit';
import { useAuthStore } from '@/stores/useAuthStore';
import { buildTenantHandoffUrl, type SsoPayload } from '@/utils/crossHostSso';
import { fetchTenantConfigForHost } from '@/services/tenantApi';

/**
 * THE ▾ BESIDE THE CLINIC OPENS A PICKER — "MAIE ▾" in Today's sub line was
 * drawn as a dropdown and did nothing. The product has no branches yet (one
 * clinic is one tenant), so the picker lists the clinics this login works
 * at — the same switch the legacy header's clinic menu does, including the
 * cross-host hand-off when the other clinic lives on its own domain.
 */
export function ClinicPicker({ open, onClose }: { open: boolean; onClose: () => void }) {
  const activeTenantId = useAuthStore((s) => s.activeTenantId);
  const available = useAuthStore((s) => s.availableTenants);
  const refresh = useAuthStore((s) => s.refreshMemberships);
  const switchTenant = useAuthStore((s) => s.switchTenant);
  const popLastTokenPayload = useAuthStore((s) => s.popLastTokenPayload);
  const [busy, setBusy] = useState<string | null>(null);
  const [hostBase, setHostBase] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!available.length) refresh().catch(() => undefined);
    fetchTenantConfigForHost().then((c) => setHostBase(c.publicHostBase)).catch(() => undefined);
  }, [open, available.length, refresh]);

  const pick = async (tenantId: string) => {
    if (tenantId === activeTenantId || busy) return onClose();
    setBusy(tenantId);
    try {
      await switchTenant(tenantId);
      const target = available.find((t) => t.tenantId === tenantId);
      const host = target?.preferredHost ?? null;
      if (host && host !== window.location.host && hostBase) {
        const payload = popLastTokenPayload();
        if (payload?.tenant?.slug) {
          window.location.replace(buildTenantHandoffUrl(payload.tenant.slug, hostBase, payload as unknown as SsoPayload));
          return;
        }
        window.location.href = `${window.location.protocol}//${host}/login`;
        return;
      }
      window.location.reload();
    } catch {
      setBusy(null);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Clinic"
      sub={available.length > 1 ? 'the clinics you work at — pick one to switch' : 'the clinic you work at'}>
      {available.length ? available.map((t) => {
        const on = t.tenantId === activeTenantId;
        return (
          <button key={t.tenantId} type="button" className="row" disabled={!!busy} onClick={() => void pick(t.tenantId)}
            style={{ width: '100%', textAlign: 'start', font: 'inherit', color: 'inherit', cursor: 'pointer',
              ...(on ? { borderColor: 'var(--brand-500)', background: 'var(--brand-100)' } : {}) }}>
            <span className="brand"><Icon name="building-hospital" size="lg" /></span>
            <div className="grow">
              <p className="t-strong">{t.displayName}</p>
              <p className="t-cap mut">{[t.role, on ? 'you are here' : busy === t.tenantId ? 'switching…' : null].filter(Boolean).join(' · ')}</p>
            </div>
            {on ? <Icon name="check" /> : null}
          </button>
        );
      }) : (
        <p className="t-sec mut">Reading your clinics…</p>
      )}
      {available.length === 1 ? (
        <p className="t-cap mut mt8">Branches of one clinic are not set up yet; a second clinic you join appears here.</p>
      ) : null}
    </Modal>
  );
}
